import BaseService from "./BaseService";
import CityStorage from "../storage/CityStorage";
import type { CityRecord } from "../storage/CityStorage";
import RestService from "./RestService";
import { normalizeCityName } from "../utils/cityName";
import { once } from "../utils/promise";

/**
 * Localidad cruda, tal y como llega en /data/city_list.json.
 *
 * normalizeCity la convierte al CityRecord que guarda CityStorage.
 */
export interface CityRawData {
    id: string | number;
    name: string;
    /** "lat,lon" en una sola cadena. */
    latlon?: string;
    size: string | number;
    priority: string | number;
}

export class CityService extends BaseService {

    /**
     * Pending city database initialization.
     */
    static initPromise: Promise<number> | undefined;

    /**
     * Pending city list download.
     */
    static loadPromise: Promise<number> | undefined;

    /**
     * Pending city list reset.
     */
    static resetPromise: Promise<string> | undefined;

    /**
     * Minimum record count for the stored city list to be considered complete.
     *
     * The full list is ~4500 cities, so anything below this means an interrupted
     * download, expired or partially wiped data: the list is downloaded again.
     */
    static minCityCount = 3500;

    /**
     * Makes sure the city database exists and contains records.
     */
    static checkAndInit(): Promise<number> {
        return once(this, 'initPromise', () => this.initializeCityList());
    }

    /**
     * Opens the database, reconnects when needed and loads missing records.
     */
    static async initializeCityList(): Promise<number> {
        let cityCount;

        // A new app version resets the list at startup (@see
        // UpdateService.checkRunningAppVersion), and that reset runs in
        // parallel with this. Wait for it, so the count below never reads
        // records that are about to be wiped: otherwise a new version could
        // skip the download and be left with an empty database.
        await this.pendingReset();

        try {
            await CityStorage.init();
            cityCount = await CityStorage.count();
        } catch (error) {
            this.logger().warn('City database connection is not available. Reopening it.', error);
            await CityStorage.reconnect();
            cityCount = await CityStorage.count();
        }

        if (cityCount >= this.minCityCount) {
            return cityCount;
        }

        this.logger().warn(`City database holds ${cityCount} of the expected ${this.minCityCount}+ records. Loading city records.`);
        return this.setCityList();
    }

    /**
     * Fetch all cities from the REST service and store them in the cities indexedDB.
     */
    static setCityList(): Promise<number> {
        return once(this, 'loadPromise', () => this.fetchAndStoreCityList());
    }

    /**
     * Downloads and stores the complete city list.
     */
    static async fetchAndStoreCityList(): Promise<number> {
        const cities = await RestService.getCityList();
        if (!Array.isArray(cities)) {
            throw new Error('Invalid city list response.');
        }

        this.logger().warn(`Loaded ${cities.length} cities`);
        // La comprobacion de arriba es la validacion; el contenido del array
        // llega sin tipar desde el REST.
        const normalizedCities = (cities as CityRawData[]).map((city) => this.normalizeCity(city));
        const storedCities = await CityStorage.setItems(normalizedCities);

        this.debug(`All cities stored with ${storedCities} cities`);
        return storedCities;
    }

    /**
     * find in db city by name
     */
    static async findCity(cityName: string, retryOnError = true): Promise<CityRecord[]> {
        try {
            await this.checkAndInit();
            const cityList = await CityStorage.find(normalizeCityName(cityName));
            cityList.sort((a, b) => {
                if (a.priority === b.priority) {
                    return b.population_size - a.population_size;
                }
                return a.priority - b.priority;
            });
            this.debug(`${cityList.length} cities found`);
            return cityList;
        } catch (error) {
            if (!retryOnError) {
                throw error;
            }

            this.logger().warn('City search failed. Reinitializing the database and retrying once.', error);
            await CityStorage.reconnect();
            // Force a fresh check so an empty DB is repopulated before retrying.
            this.initPromise = undefined;
            await this.checkAndInit();
            return this.findCity(cityName, false);
        }
    }

    /**
     * clean  cities indexedDB contents
     *
     * Opens the database first: nothing else opens it at startup anymore, the
     * city list is loaded later and in the background (@see App.loadCityList).
     */
    static resetCityList(): Promise<string> {
        return once(this, 'resetPromise', async () => {
            await CityStorage.init();
            return CityStorage.clear();
        });
    }

    /**
     * Waits for a city list reset in flight, if any.
     *
     * A failed reset is not fatal: the record count decides whether to download
     * anyway, and the caller of resetCityList reports the failure.
     */
    static async pendingReset(): Promise<void> {
        try {
            await this.resetPromise;
        } catch {
            // Handled where the reset was requested.
        }
    }

    /**
     * Normalizes raw city data from the REST service to the cities DB schema.
     */
    static normalizeCity(cityData: CityRawData): CityRecord {
        const latlon = (cityData.latlon || '').split(',').map((value) => parseFloat(value));
        return {
            id: Number.parseInt(String(cityData.id), 10),
            name: cityData.name,
            geometry: latlon.reverse(),
            population_size: Number.parseInt(String(cityData.size), 10),
            priority: Number.parseInt(String(cityData.priority), 10),
        }
    }
}
